import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { Card, Form, Col } from "react-bootstrap";
import useMessage from "hooks/useMessage";
import useExceptionHandler from "hooks/useExceptionHandler";
import { Simplebar } from "components";
import DropZone from "components/DropZone/Dropzone";
import { CategoriaLookup } from "components/Lookups/CategoriaLookup/CategoriaLookup";
import IconButton from "components/IconButton/IconButton";
import SectionRow from "components/PantallasEdit/SectionRow/SectionRow";
import EditContainer from "components/PantallasEdit/EditContainer/EditContainer";
import InputContainer from "components/PantallasEdit/InputContainer/InputContainer";
import EditSection from "components/PantallasEdit/EditSection/EditSection";
import { ItemManager } from "managers";
import { Item } from "models/ECF/Item";
import { StyleHelper } from "utils";

export default function ItemEdit({
  showLoading,
  hideLoading,
  IdSec = undefined,
  showCanvas,
  setShowCanvas,
  esCreacionExterna = false,
  updateList = () => { },
  onCreated = () => { },
}) {

  const [esNuevo, setEsNuevo] = useState(!IdSec);
  const [item, setItem] = useState({ Estatus: Item.DEFAULT_ESTATUS_VALUE });
  const [tituloHeader, setTituloHeader] = useState("Crear Item");
  const ref = useRef(null);
  const { Message, cerrarAlertas } = useMessage();
  const { handleException } = useExceptionHandler();

  useEffect(() => {
    if (IdSec) {
      showLoading();
      cargarItem(IdSec);
      setTituloHeader("Editar Item");
    } else {
      setDefaultValues();
      setTituloHeader("Crear Item");
    }
    hideLoading();
    cerrarAlertas();
    return () => { };
  }, [IdSec]);

  const setDefaultValues = () => {
    setItem({
      Nombre: "",
      Descripcion: "",
      IndicadorFacturacion: "",
      IndicadorBienOServicio: "1",
      PrecioUnitario: "",
      Estatus: Item.DEFAULT_ESTATUS_VALUE,
      Img: [],
      EsVendible: false,
      EsComprable: false,
      TipoISC: null,
      OtroImpuesto: null,
    });
  };

  const cargarItem = (IdSec) => {
    showLoading();
    ItemManager.buscarPorId(IdSec)
      .then((data) => {
        if (data && data.IdSec) {
          setItem({
            ...data,
            Img: [],
            Estatus: data.Estatus ?? Item.DEFAULT_ESTATUS_VALUE,
          });
        } else {
          Message({ message: "Item no encontrado", type: "error" });
        }
      })
      .catch((e) => handleException(e))
      .finally(() => {
        hideLoading();
        updateList();
      });
  };

  function grabar() {
    showLoading();

    const payload = {
      ...item,
      Estatus: item.Estatus ?? Item.DEFAULT_ESTATUS_VALUE,
    };

    if (esNuevo) {
      ItemManager.crear(payload)
        .then((_res) => {
          Message({ message: `Grabado Exitosamente` });
          if (!esCreacionExterna) {
            setEsNuevo(false);
            cargarItem(_res.IdSec);
            IdSec = _res.IdSec;
          }
          else {
            onCreated(_res);
            setDefaultValues();
            setShowCanvas(false);
          }
        })
        .catch((e) => handleException(e))
        .finally(() => {
          hideLoading();
          updateList();
        });
    } else {
      ItemManager.actualizar(payload)
        .then((res) => {
          Message({ message: `Actualizado Exitosamente` });
          if (!esCreacionExterna) {
            cargarItem(payload.IdSec);
          }
          else {
            setDefaultValues();
            setShowCanvas(false);
          }
        })
        .catch((e) => handleException(e))
        .finally(() => {
          hideLoading();
          updateList();
        });
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setItem({
      ...item,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleCategoriaChange = (event) => {
    if (event && event.value && event.value !== item.CategoriaIdSec) {
      setItem({
        ...item,
        CategoriaId: event.value,
      });
    } else {
      setItem({
        ...item,
        CategoriaId: undefined,
      });
    }
  };

  const onFileChange = (files) => {
    setItem({ ...item, Img: files, _EliminarImagen: (files?.length ?? 0) === 0, ImagenBase64: null });
  };

  return (
    <Simplebar
      ref={ref}
      backdrop={"static"}
      confirmBack
      placement={"end"}
      size="xxl"
      portal={false}
      visible={showCanvas}
      setShow={(e) => {
        setShowCanvas(e);
      }}
      useLoading={true}
      title={tituloHeader}
      btnLeft={{
        show: true,
        message: "Grabar",
        isComponent: true,
        size: "45px",
        height: "24px",
        with: "24px",
        iconName: "save",
        isCenter: true,
        func: grabar,
      }}
      btnRight={{
        show: false,
        label: "Grabar",
        func: () => { },
      }}
    >
      <Col>
        <Form action="" className="form-horizontal" method="">
          <EditContainer>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              {item?.ImagenBase64 ? (
                <InputContainer
                  labelClassName={'d-flex'}
                  rowStyle={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}
                  style={{ display: 'flex', justifyContent: 'center', width: '100%', textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div >
                      <img src={
                        item.ImagenBase64?.startsWith("data:image")
                          ? item.ImagenBase64
                          : `data:image/png;base64,${item.ImagenBase64}`
                      } alt="Imagen seleccionada"
                        style={{ maxWidth: "300px", maxHeight: "300px" }} />
                    </div>

                    <IconButton styles={{ height: '2.5rem' }}  >
                      <i className="fa fa-trash" onClick={() => setItem({ ...item, ImagenBase64: null, _EliminarImagen: true })}></i>
                    </IconButton>
                  </div>

                </InputContainer>
              ) : (
                <InputContainer
                  label="Imagen"
                  labelClassName={'d-flex'}
                  rowStyle={{ display: 'flex', justifyContent: 'center', width: '50%', marginBottom: '1rem' }}
                  style={{ display: 'flex', justifyContent: 'center', width: '100%', textAlign: 'center' }}>
                  <DropZone
                    label="Seleccionar Imagen"
                    updateFilesCb={onFileChange}
                    multiple={false}
                    type="image" />
                </InputContainer>
              )}
            </div>

            <EditSection title="Informaciones Requeridas" style={{}}>
              <SectionRow>
                <InputContainer stacked label="Nombre" labelClassName={'d-flex'}>
                  <Form.Control
                    type="text"
                    name="Nombre"
                    value={item.Nombre}
                    onChange={handleChange}
                    style={StyleHelper.InputRequired}
                  />
                </InputContainer>

                <InputContainer stacked label="Precio" labelClassName={'d-flex'}>
                  <Form.Control
                    type="number"
                    name="PrecioUnitario"
                    value={item.PrecioUnitario}
                    placeholder="Precio Unitario"
                    onChange={handleChange}
                    style={StyleHelper.InputRequired}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <InputContainer stacked label="Tipo de Item" labelClassName={"d-flex"}>
                  <div style={{ display: "flex", gap: "1.5rem" }}>
                    {Item.INDICADOR_BIEN_O_SERVICIO_LIST.map((option) => (
                      <Form.Check
                        key={option.value}
                        id={`IndicadorBienOServicio-${option.value}`}
                        type="radio"
                        name="IndicadorBienOServicio"
                        value={option.value}
                        checked={item.IndicadorBienOServicio == option.value}
                        onChange={handleChange}
                        label={option.label}
                      />
                    ))}
                  </div>
                </InputContainer>

                <InputContainer stacked label="Estatus" labelClassName={"d-flex"}>
                  <div style={{ display: "flex", gap: "1.5rem" }}>
                    {Item.ESTATUS_LIST.map((option) => (
                      <Form.Check
                        key={option.value}
                        id={`Estatus-${option.value}`}
                        type="radio"
                        name="Estatus"
                        value={option.value}
                        checked={(item.Estatus || Item.ESTATUS_LIST[0].value) === option.value}
                        onChange={(e) => {
                          setItem({
                            ...item,
                            Estatus: e.target.value,
                          });
                        }}
                        label={option.label}
                        disabled={esNuevo}
                      />
                    ))}
                  </div>
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <SectionRow>
                  <InputContainer stacked label="Comportamiento" labelClassName={"d-flex"}>
                    <div style={{ display: "flex", gap: "1.5rem" }}>
                      <Form.Check
                        id="EsVendible"
                        type="checkbox"
                        name="EsVendible"
                        checked={item.EsVendible || false}
                        onChange={handleChange}
                        label="Vendible"
                      />

                      <Form.Check
                        id="EsComprable"
                        type="checkbox"
                        name="EsComprable"
                        checked={item.EsComprable || false}
                        onChange={handleChange}
                        label="Comprable"
                      />
                    </div>
                  </InputContainer>
                </SectionRow>
              </SectionRow>
            </EditSection>

            <EditSection title="Impuestos">
              <SectionRow triple={true}>
                <InputContainer stacked label="Tipo de ITBIS" labelClassName={'d-flex'}>
                  <Select
                    className="react-select primary required"
                    classNamePrefix="react-select"
                    name="IndicadorFacturacion"
                    options={Item.INDICADOR_FACTURACION_LIST}
                    value={Item.INDICADOR_FACTURACION_LIST.find(function (option) {
                      return (
                        option.value === item.IndicadorFacturacion
                      );
                    })}
                    onChange={(e) => {
                      setItem({
                        ...item,
                        IndicadorFacturacion: e.value
                      });
                    }}
                  />
                </InputContainer>

                <InputContainer stacked label="Tipo de ISC" labelClassName={'d-flex'}>
                  <Select
                    className="react-select primary"
                    classNamePrefix="react-select"
                    name="TipoISC"
                    isClearable
                    options={Item.TIPO_ISC_LIST}
                    value={Item.TIPO_ISC_LIST.find((option) => option.value === item.TipoISC) ?? null}
                    onChange={(e) => {
                      setItem({ ...item, TipoISC: e ? e.value : null });
                    }}
                  />
                </InputContainer>

                <InputContainer stacked label="Otro Impuesto" labelClassName={'d-flex'}>
                  <Select
                    className="react-select primary"
                    classNamePrefix="react-select"
                    name="OtroImpuesto"
                    isClearable
                    options={Item.OTRO_IMPUESTO_LIST}
                    value={Item.OTRO_IMPUESTO_LIST.find((option) => option.value === item.OtroImpuesto) ?? null}
                    onChange={(e) => {
                      setItem({ ...item, OtroImpuesto: e ? e.value : null });
                    }}
                  />
                </InputContainer>
              </SectionRow>
            </EditSection>

            <EditSection title="Otras Informaciones">
              <SectionRow>
                <InputContainer stacked label="Descripción" labelClassName={"d-flex"}>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="Descripcion"
                    value={item.Descripcion || ""}
                    onChange={handleChange}
                  />
                </InputContainer>

                <div style={{ display: "flex", flexDirection: "column", gap: "-1rem" }}>
                  <InputContainer stacked label="Categoría" labelClassName={'d-flex'}>
                    <CategoriaLookup
                      initialValue={{ value: item.CategoriaId }}
                      onChange={(e) => handleCategoriaChange(e)}
                    />
                  </InputContainer>

                  <InputContainer stacked label="Código de Barra" labelClassName={'d-flex'}>
                    <Form.Control
                      type="text"
                      name="CodigoBarra"
                      value={item.CodigoBarra || ""}
                      onChange={handleChange}
                    />
                  </InputContainer>
                </div>
              </SectionRow>
            </EditSection>
          </EditContainer>
        </Form>
      </Col>
    </Simplebar>
  );
}